import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import InputMask from "react-input-mask";
import classes from "./pay3.module.css";
import Api from "../../Api";
import InstructionCarousel from "./InstructionCarousel";

const api = new Api({
    token: localStorage.getItem("AUTH_TOKEN"),
});

const PAYMENT_TIMEOUT_SECONDS = 15 * 60; // 15 минут
const STATUS_POLL_INTERVAL_MS = 10000; // 10 секунд

function getUrlParams() {
    if (typeof window === "undefined") return {
        widg: null,
        unlid: null,
        email: null,
        phone: null,
        amount: null
    };
    const params = new URLSearchParams(window.location.search);
    return {
        widg: params.get("widg"),
        unlid: params.get("unlid"),
        email: params.get("email"),
        phone: params.get("phone"),
        amount: params.get("amount"),
    };
}

const isIOS = () =>
    typeof navigator !== "undefined" &&
    /iPhone|iPad|iPod/i.test(navigator.userAgent);

const isAndroid = () =>
    typeof navigator !== "undefined" &&
    /Android/i.test(navigator.userAgent);

const bankLabels = {
    tbank: "T-Bank",
    sber: "Сбербанк",
    sber_hydra: "Сбербанк",
    sber_craftlite: "Сбербанк",
};

// Дефолтные лимиты (переопределяются из конфига виджета)
// NX (Hydra): 1000-110000, Флакон (Craftlite): 500-20000, NX+Флакон: 1000-20000
const DEFAULT_minAmount = 1000;
const DEFAULT_maxAmount = 110000;

const normalizeAmount = function (amt) {
    const num = parseFloat(String(amt).replace(/\s/g, ""));
    if (Number.isNaN(num)) return NaN;
    return Math.round(num);
};

const TRANSFER_PATH = "/PayTransfer";

function buildTransferUrl(bank, card, amt, recipientName) {
    const params = new URLSearchParams();
    if (bank) params.set("bank", bank);
    if (card) params.set("card", card);
    if (amt) params.set("amount", amt);
    if (recipientName) params.set("name", recipientName);

    const qs = params.toString();
    const suffix = qs ? "?" + qs : "";

    if (typeof window !== "undefined" && window.location && window.location.origin) {
        return window.location.origin + TRANSFER_PATH + suffix;
    }

    return TRANSFER_PATH + suffix;
}

function CopyIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            aria-hidden="true"
            focusable="false"
        >
            <rect
                x="5"
                y="3"
                width="8"
                height="10"
                rx="2"
                ry="2"
                fill="none"
                stroke="#4b5563"
                strokeWidth="1.2"
            />
            <rect
                x="3"
                y="5"
                width="8"
                height="8"
                rx="2"
                ry="2"
                fill="none"
                stroke="#9ca3af"
                strokeWidth="1.2"
            />
        </svg>
    );
}

function buildBankDeepLinks(bank, platform, card, amt) {
    if (!card) {
        return {primaryUrl: "", allUrls: []};
    }

    if (bank === "tbank") {
        const params =
            "amount=" +
            encodeURIComponent(amt) +
            "&targetCardNumber=" +
            encodeURIComponent(card);

        if (platform === "ios") {
            const url = "bank100000000004://Main/Pay/C2C?" + params;
            return {primaryUrl: url, allUrls: [url]};
        }

        const url = "tinkoffbank://Main/Pay/C2C?" + params;
        return {primaryUrl: url, allUrls: [url]};
    }

    if (bank === "sber") {
        if (platform === "ios") {
            const params =
                "amount=" +
                encodeURIComponent(amt) +
                "&isNeedToOpenNextScreen=true" +
                "&skipContactsScreen=true" +
                "&to=" +
                encodeURIComponent(card) +
                "&type=cardNumber";

            const paramsP2P =
                "type=card_number" +
                "&requisiteNumber=" +
                encodeURIComponent(card);

            const urls = [
                "sberbankonline://payments/p2ptransfer?" + params,
                "sberbankidlogin://payments/p2ptransfer?" + params,
                "sbolonline://payments/p2ptransfer?" + params,
                "onlineappmobile://sbolonline/p2ptransfer?" + params,
                "onlineios-app://sbolonline/p2ptransfer?" + params,
                "budgetonline-ios://sbolonline/p2ptransfer?" + params,
                "ios-app-smartonline://sbolonline/p2ptransfer?" + params,
                "sberbankonline://payments/p2p?" + paramsP2P,
                "sberbankidlogin://payments/p2p?" + paramsP2P,
                "sbolonline://payments/p2p?" + paramsP2P,
            ];

            return {primaryUrl: urls[0], allUrls: urls};
        }

        if (platform === "android") {
            const params =
                "type=card_number" +
                "&requisiteNumber=" +
                encodeURIComponent(card);

            const urls = [
                "intent://ru.sberbankmobile/payments/p2p?" + params,
                "intent://ru.sberbankmobile/android-app/payments/p2p?" + params,
                "android-app://ru.sberbankmobile/payments/p2p?" + params,
                "android-app://ru.sberbankmobile_alpha/payments/p2p?" + params,
                "sberbankonline://payments/p2p?" + params,
            ];

            return {primaryUrl: urls[0], allUrls: urls};
        }

        const paramsP2P =
            "type=card_number" +
            "&requisiteNumber=" +
            encodeURIComponent(card);

        const url = "sberbankonline://payments/p2p?" + paramsP2P;

        return {primaryUrl: url, allUrls: [url]};
    }

    return {primaryUrl: "", allUrls: []};
}

function attemptOpenDeepLinks(urls) {
    if (!urls || !urls.length) return;

    let index = 0;

    const openNext = () => {
        const url = urls[index];

        try {
            window.location.href = url;
        } catch (e) {
            console.error("Failed to open deeplink", url, e);
        }

        index += 1;
        if (index < urls.length) {
            setTimeout(openNext, 1200);
        }
    };

    openNext();
}

function SuccessIcon() {
    return (
        <svg
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
            style={{display: "block", margin: "0 auto 16px"}}
        >
            <circle cx="32" cy="32" r="30" fill="#22c55e"/>
            <path
                d="M20 32l8 8 16-16"
                stroke="#fff"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
            />
        </svg>
    );
}

function ErrorIcon() {
    return (
        <svg
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
            style={{display: "block", margin: "0 auto 16px"}}
        >
            <circle cx="32" cy="32" r="30" fill="#ef4444"/>
            <path
                d="M22 22l20 20M42 22l-20 20"
                stroke="#fff"
                strokeWidth="4"
                strokeLinecap="round"
                fill="none"
            />
        </svg>
    );
}

function formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

export default function Pay3() {
    const {widg, unlid, email: urlEmail, phone: urlPhone, amount: urlAmount} = useMemo(() => getUrlParams(), []);

    const [email, setEmail] = useState(urlEmail || "");
    const [phone, setPhone] = useState(urlPhone || "");
    const [amount, setAmount] = useState(urlAmount || "");
    const [orderId, setOrderId] = useState(null);
    const [loadingBank, setLoadingBank] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [qrModal, setQrModal] = useState(null);

    const [paymentStatus, setPaymentStatus] = useState(null);
    const [remainingSeconds, setRemainingSeconds] = useState(PAYMENT_TIMEOUT_SECONDS);
    const [, setIsPolling] = useState(false);

    // Конфигурация кнопок виджета
    const [widgetButtons, setWidgetButtons] = useState([]);
    const [configLoading, setConfigLoading] = useState(true);
    const [, setConfigError] = useState(null);
    const [minAmount, setMinAmount] = useState(DEFAULT_minAmount);
    const [maxAmount, setMaxAmount] = useState(DEFAULT_maxAmount);

    const timerIntervalRef = useRef(null);
    const pollingIntervalRef = useRef(null);
    const paymentStartTimeRef = useRef(null);
    const serverTimeOffsetRef = useRef(0);

    const stopTimer = useCallback(() => {
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        }
    }, []);

    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        setIsPolling(false);
    }, []);

    const checkPaymentStatus = useCallback(async () => {
        if (!widg || !unlid) return;

        try {
            const resp = await api.checkWidgetStatus({widg, unlid});

            if (resp && resp.status) {
                setPaymentStatus(resp.status);

                if (typeof resp.remainingSeconds === "number") {
                    const serverRemaining = resp.remainingSeconds;
                    const elapsed = Math.floor((Date.now() - paymentStartTimeRef.current) / 1000);
                    const clientRemaining = Math.max(0, PAYMENT_TIMEOUT_SECONDS - elapsed);
                    serverTimeOffsetRef.current = serverRemaining - clientRemaining;
                    setRemainingSeconds(serverRemaining);
                }

                if (["COMPLETED", "DECLINED", "EXPIRED"].includes(resp.status)) {
                    stopPolling();
                    stopTimer();
                }
            }
        } catch (e) {
            console.error("Status check error:", e);
        }
    }, [widg, unlid, stopPolling, stopTimer]);

    const startPaymentTracking = useCallback(() => {
        paymentStartTimeRef.current = Date.now();
        serverTimeOffsetRef.current = 0;
        setRemainingSeconds(PAYMENT_TIMEOUT_SECONDS);
        setPaymentStatus("PENDING");
        setIsPolling(true);

        timerIntervalRef.current = setInterval(() => {
            const elapsed = Math.floor((Date.now() - paymentStartTimeRef.current) / 1000);
            const clientRemaining = Math.max(0, PAYMENT_TIMEOUT_SECONDS - elapsed);
            const syncedRemaining = Math.max(0, clientRemaining + serverTimeOffsetRef.current);
            setRemainingSeconds(syncedRemaining);

            if (syncedRemaining <= 0) {
                setPaymentStatus("EXPIRED");
                stopTimer();
                stopPolling();
            }
        }, 1000);

        pollingIntervalRef.current = setInterval(() => {
            checkPaymentStatus();
        }, STATUS_POLL_INTERVAL_MS);

        checkPaymentStatus();
    }, [checkPaymentStatus, stopTimer, stopPolling]);

    useEffect(() => {
        return () => {
            stopTimer();
            stopPolling();
        };
    }, [stopTimer, stopPolling]);

    // Загрузка конфигурации виджета при старте
    useEffect(() => {
        async function loadWidgetConfig() {
            if (!widg) {
                setConfigLoading(false);
                setConfigError("Отсутствует параметр widg в URL");
                return;
            }

            try {
                const config = await api.getWidgetConfig({widg});
                if (config && Array.isArray(config.buttons)) {
                    setWidgetButtons(config.buttons);
                    // Устанавливаем лимиты из конфига
                    if (config.minAmount) setMinAmount(config.minAmount);
                    if (config.maxAmount) setMaxAmount(config.maxAmount);
                } else {
                    // Фоллбек: если кнопки не пришли - показываем стандартные
                    setWidgetButtons([
                        {id: 'sber', bank: 'sber', provider: 'hydra', label: 'Сбербанк'},
                        {id: 'tbank', bank: 'tbank', provider: 'hydra', label: 'T-Bank'},
                    ]);
                }
            } catch (e) {
                console.error("Failed to load widget config:", e);
                // Фоллбек на стандартную конфигурацию
                setWidgetButtons([
                    {id: 'sber', bank: 'sber', provider: 'hydra', label: 'Сбербанк'},
                    {id: 'tbank', bank: 'tbank', provider: 'hydra', label: 'T-Bank'},
                ]);
            } finally {
                setConfigLoading(false);
            }
        }

        loadWidgetConfig();
    }, [widg]);

    const validateEmail = function (emailValue) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);
    };

    const validatePhone = function (phoneValue) {
        return phoneValue.replace(/\D/g, "").length === 11;
    };

    const validateAmount = function (amt) {
        const num = normalizeAmount(amt);
        return num >= minAmount && num <= maxAmount;
    };

    const formatAmount = function (value) {
        return value.replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    };

    function createOrder() {
        if (!validateEmail(email)) {
            setErrorMessage("Введите корректный email");
            return;
        }
        if (!validatePhone(phone)) {
            setErrorMessage("Введите корректный телефон");
            return;
        }
        if (!validateAmount(amount)) {
            setErrorMessage(
                `Сумма должна быть от ${minAmount.toLocaleString(
                    "ru-RU"
                )} до ${maxAmount.toLocaleString("ru-RU")} ₽`
            );
            return;
        }

        const id = Math.floor(100000 + Math.random() * 900000);
        setOrderId(id);
        setErrorMessage("");
    }

    function copyToClipboard(text) {
        if (!text) return;
        try {
            if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).catch((e) => {
                    console.error("Clipboard write failed", e);
                });
            } else {
                const el = document.createElement("textarea");
                el.value = text;
                document.body.appendChild(el);
                el.select();
                try {
                    document.execCommand("copy");
                } catch (err) {
                    console.error("execCommand copy failed", err);
                }
                document.body.removeChild(el);
            }
        } catch (e) {
            console.error("Copy error", e);
        }
    }

    async function handlePay(buttonConfig) {
        // buttonConfig = { id, bank, provider, label }
        const bank = buttonConfig.bank;
        const provider = buttonConfig.provider;
        const buttonId = buttonConfig.id;

        if (!widg || !unlid) {
            setErrorMessage("Отсутствуют обязательные параметры в URL (widg, unlid)");
            return;
        }
        if (!orderId) {
            setErrorMessage("Сначала создайте заказ");
            return;
        }
        if (!validateAmount(amount)) {
            setErrorMessage(
                `Сумма должна быть от ${minAmount.toLocaleString(
                    "ru-RU"
                )} до ${maxAmount.toLocaleString("ru-RU")} ₽`
            );
            return;
        }

        const amtNumber = normalizeAmount(amount);
        if (!Number.isFinite(amtNumber)) {
            setErrorMessage("Некорректная сумма");
            return;
        }

        try {
            setLoadingBank(buttonId);
            setErrorMessage("");
            setQrModal(null);

            const reqBody = {
                amount: amtNumber,
                currency: "RUB",
                bank,
                widg,
                unlid,
                provider,
            };

            const resp = await api.createMobilePay(reqBody);

            if (resp && resp.provider === "craftlite") {
                if (!resp.qrUrl) {
                    setErrorMessage("Не удалось получить ссылку для оплаты");
                    return;
                }

                setQrModal({
                    provider: "craftlite",
                    url: resp.qrUrl,
                    amount: amtNumber,
                    card: null,
                    recipientName: null,
                    bank: null,
                });

                startPaymentTracking();
                return;
            }

            if (!resp || !resp.cardNumber) {
                setErrorMessage("Не удалось получить реквизиты для оплаты");
                return;
            }

            const card = resp.cardNumber;
            const amt = String(amtNumber);

            const ext = resp.externalRefs || {};
            const recipientName =
                ext.name ||
                resp.recipientName ||
                resp.cardHolder ||
                resp.holderName ||
                resp.name ||
                "";

            const platform = isIOS()
                ? "ios"
                : isAndroid()
                    ? "android"
                    : "other";

            const {primaryUrl, allUrls} = buildBankDeepLinks(
                bank,
                platform,
                card,
                amt
            );

            const transferUrl = buildTransferUrl(
                bank,
                card,
                amt,
                recipientName
            );

            setQrModal({
                provider: "hydra",
                bank,
                url: transferUrl,
                amount: amtNumber,
                card,
                recipientName,
            });

            startPaymentTracking();

            if ((platform === "ios" || platform === "android") && primaryUrl) {
                setTimeout(() => {
                    attemptOpenDeepLinks(allUrls);
                }, 500);
            }
        } catch (e) {
            console.error(e);

            let msg = "Неизвестная ошибка";
            if (e) {
                const resp = e.response;
                if (resp && resp.data) {
                    if (resp.data.message) {
                        msg = resp.data.message;
                    } else if (resp.data.error) {
                        msg = resp.data.error;
                    } else if (e.message) {
                        msg = e.message;
                    }
                } else if (e.message) {
                    msg = e.message;
                }
            }

            setErrorMessage("Ошибка при создании платежа: " + msg);
        } finally {
            setLoadingBank(null);
        }
    }

    const instructionImages = {
        sber: [
            require("./images/sber/step1.jpg"),
            require("./images/sber/step2.png"),
            require("./images/sber/step3.png"),
            require("./images/sber/step4.png"),
            require("./images/sber/step5.png"),
            require("./images/sber/step6.png"),
            require("./images/sber/step7.jpg"),
        ],
        tbank: [
            require("./images/tbank/step1.jpg"),
            require("./images/tbank/step2.jpg"),
            require("./images/tbank/step3.jpg"),
            require("./images/tbank/step4.jpg"),
        ],
    };

    return (
        <div className={classes.Page}>
            <div className={classes.Card}>
                <header className={classes.CardHeader}>
                    <h2 className={classes.Title}>Оплата через банк</h2>
                </header>

                <div className={classes.FormGroup}>
                    <label>Email</label>
                    <input
                        type="email"
                        placeholder="example@mail.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </div>

                <div className={classes.FormGroup}>
                    <label>Телефон</label>
                    <InputMask
                        mask="+7 999 999-99-99"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                    >
                        {(props) => (
                            <input
                                {...props}
                                type="text"
                                placeholder="+7 900 000-00-00"
                            />
                        )}
                    </InputMask>
                </div>

                <div className={classes.FormGroup}>
                    <label>Сумма (₽)</label>
                    <input
                        type="text"
                        value={amount}
                        onChange={(e) => setAmount(formatAmount(e.target.value))}
                        placeholder={String(minAmount)}
                    />
                    <span className={classes.Hint}>
                        Сумма от {minAmount.toLocaleString("ru-RU")} до {maxAmount.toLocaleString("ru-RU")} ₽
                    </span>
                </div>

                {!orderId && (
                    <button
                        type="button"
                        className={classes.CreateBtn}
                        onClick={createOrder}
                        disabled={!!loadingBank}
                    >
                        Создать заказ
                    </button>
                )}

                {orderId && (
                    <div className={classes.PaymentBlock}>
                        <div className={classes.BanksTitle}>
                            Выберите способ оплаты
                        </div>

                        {configLoading ? (
                            <div className={classes.LoadingText}>Загрузка...</div>
                        ) : widgetButtons.length === 0 ? (
                            <div className={classes.LoadingText}>Способы оплаты недоступны</div>
                        ) : (
                            widgetButtons.map((btn) => {
                                // Определяем класс кнопки и иконку по bank
                                let btnClass = classes.PayBtnSber;
                                let iconSrc = "/img/sber-logo-eng.svg";
                                let iconClass = classes.PayIcon;

                                if (btn.bank === 'tbank') {
                                    btnClass = classes.PayBtnTinkoff;
                                    iconSrc = "/img/t-bank-logo-en.svg";
                                    iconClass = classes.PayIcon + " " + classes.PayIconTinkoff;
                                }
                                // Все кнопки sber используют стандартный зеленый стиль

                                return (
                                    <button
                                        key={btn.id}
                                        type="button"
                                        className={btnClass}
                                        onClick={() => handlePay(btn)}
                                        disabled={!!loadingBank}
                                    >
                                        <img
                                            src={iconSrc}
                                            alt={bankLabels[btn.bank] || btn.bank}
                                            className={iconClass}
                                        />
                                    </button>
                                );
                            })
                        )}

                        <p className={classes.SmallNote}>
                            После выбора способа оплаты мы передадим сумму и реквизиты в
                            приложение или покажем QR-код. Отсканируйте QR-код с
                            телефона — он откроет страницу оплаты, которая запустит
                            приложение банка.
                        </p>
                    </div>
                )}
            </div>

            {loadingBank && (
                <div className={classes.LoadingOverlay}>
                    <div className={classes.LoadingBox}>
                        <div className={classes.LoadingSpinner}/>
                        <div className={classes.LoadingText}>
                            Создаём платёж в {bankLabels[loadingBank]}…
                        </div>
                        <div className={classes.LoadingSubtext}>
                            Не закрывайте страницу — сейчас откроется приложение
                            банка или появится QR-код.
                        </div>
                        <div className={classes.LoadingSubtext}>
                            Важно: укажите точную сумму для оплаты заказа в
                            приложении банка.
                        </div>
                    </div>
                </div>
            )}

            {errorMessage && (
                <div className={classes.LoadingOverlay}>
                    <div className={classes.LoadingBox}>
                        <div className={classes.LoadingText}>Ошибка</div>
                        <div className={classes.LoadingSubtext}>
                            {errorMessage}
                        </div>
                        <button
                            type="button"
                            className={classes.CreateBtn}
                            onClick={() => setErrorMessage("")}
                        >
                            Понятно
                        </button>
                    </div>
                </div>
            )}

            {qrModal && (
                <div className={classes.ModalOverlay}>
                    <div className={classes.ModalContent}>
                        {paymentStatus === "COMPLETED" ? (
                            <React.Fragment>
                                <SuccessIcon/>
                                <div className={classes.LoadingText} style={{color: "#22c55e"}}>
                                    Оплата успешно завершена!
                                </div>
                                <div className={classes.LoadingSubtext}>
                                    Спасибо за оплату. Ваш платёж обработан.
                                </div>
                                <button
                                    type="button"
                                    className={classes.CreateBtn}
                                    style={{background: "#22c55e"}}
                                    onClick={() => {
                                        setQrModal(null);
                                        setPaymentStatus(null);
                                        stopPolling();
                                        stopTimer();
                                    }}
                                >
                                    Готово
                                </button>
                            </React.Fragment>
                        ) : paymentStatus === "DECLINED" ? (
                            <React.Fragment>
                                <ErrorIcon/>
                                <div className={classes.LoadingText} style={{color: "#ef4444"}}>
                                    Платёж отклонён
                                </div>
                                <div className={classes.LoadingSubtext}>
                                    К сожалению, платёж не был выполнен. Попробуйте ещё раз.
                                </div>
                                <button
                                    type="button"
                                    className={classes.CreateBtn}
                                    onClick={() => {
                                        setQrModal(null);
                                        setPaymentStatus(null);
                                        stopPolling();
                                        stopTimer();
                                    }}
                                >
                                    Закрыть
                                </button>
                            </React.Fragment>
                        ) : paymentStatus === "EXPIRED" ? (
                            <React.Fragment>
                                <ErrorIcon/>
                                <div className={classes.LoadingText} style={{color: "#ef4444"}}>
                                    Время оплаты истекло
                                </div>
                                <div className={classes.LoadingSubtext}>
                                    Время, отведённое на оплату (15 минут), истекло. Пожалуйста, создайте новый платёж.
                                </div>
                                <button
                                    type="button"
                                    className={classes.CreateBtn}
                                    onClick={() => {
                                        setQrModal(null);
                                        setPaymentStatus(null);
                                        stopPolling();
                                        stopTimer();
                                    }}
                                >
                                    Закрыть
                                </button>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <div className={classes.CompactTimer}>
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        style={{flexShrink: 0}}
                                    >
                                        <circle
                                            cx="12"
                                            cy="12"
                                            r="9"
                                            stroke={remainingSeconds < 60 ? "#ef4444" : remainingSeconds < 180 ? "#f59e0b" : "#22c55e"}
                                            strokeWidth="2"
                                        />
                                        <path
                                            d="M12 6v6l4 2"
                                            stroke={remainingSeconds < 60 ? "#ef4444" : remainingSeconds < 180 ? "#f59e0b" : "#22c55e"}
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    <span className={classes.CompactTimerText}>
                                        Осталось:
                                    </span>
                                    <span
                                        className={classes.CompactTimerValue}
                                        style={{
                                            color: remainingSeconds < 60 ? "#ef4444" :
                                                remainingSeconds < 180 ? "#f59e0b" : "#22c55e"
                                        }}
                                    >
                                        {formatTime(remainingSeconds)}
                                    </span>
                                </div>

                                {qrModal.provider === "craftlite" ? (
                                    <React.Fragment>
                                        <div className={classes.LoadingText}>
                                            Отсканируйте QR-код для оплаты
                                        </div>

                                        <div className={classes.QrCodeContainer}>
                                            <img
                                                src={
                                                    "https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=" +
                                                    encodeURIComponent(qrModal.url)
                                                }
                                                alt="QR для оплаты"
                                                className={classes.QrCodeImage}
                                            />
                                        </div>

                                        <div className={classes.QrDetails}>
                                            <div className={classes.QrRow}>
                                                <div className={classes.QrRowMain}>
                                                    <div className={classes.QrRowLabel}>Сумма к оплате</div>
                                                    <div className={classes.QrRowValueStrong}>
                                                        {qrModal.amount.toLocaleString("ru-RU")} ₽
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        copyToClipboard(String(qrModal.amount))
                                                    }
                                                    className={classes.QrCopyBtn}
                                                >
                                                    <CopyIcon/>
                                                </button>
                                            </div>
                                        </div>

                                        <div className={classes.LoadingSubtext}>
                                            Откройте приложение банка и отсканируйте QR-код
                                            или нажмите на кнопку ниже.
                                        </div>
                                        <button
                                            type="button"
                                            className={classes.CreateBtn}
                                            onClick={() => {
                                                window.location.href = qrModal.url;
                                            }}
                                        >
                                            Открыть страницу оплаты
                                        </button>
                                        <button
                                            type="button"
                                            className={classes.SecondaryBtn}
                                            onClick={() => {
                                                setQrModal(null);
                                                setPaymentStatus(null);
                                                stopPolling();
                                                stopTimer();
                                            }}
                                        >
                                            Отмена
                                        </button>
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <div className={classes.LoadingText}>
                                            Отсканируйте QR-код для оплаты в {bankLabels[qrModal.bank]}
                                        </div>

                                        <div className={classes.QrCodeContainer}>
                                            <img
                                                src={
                                                    "https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=" +
                                                    encodeURIComponent(qrModal.url)
                                                }
                                                alt="QR для оплаты"
                                                className={classes.QrCodeImage}
                                            />
                                        </div>

                                        <div className={classes.QrDetails}>
                                            <div className={classes.QrDetailsTitle}>
                                                Реквизиты для оплаты
                                            </div>

                                            <div className={classes.QrRow}>
                                                <div className={classes.QrRowMain}>
                                                    <div className={classes.QrRowLabel}>№</div>
                                                    <div className={classes.QrRowValueMono}>
                                                        {qrModal.card}
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => copyToClipboard(qrModal.card)}
                                                    className={classes.QrCopyBtn}
                                                >
                                                    <CopyIcon/>
                                                </button>
                                            </div>

                                            <div className={classes.QrRow}>
                                                <div className={classes.QrRowMain}>
                                                    <div className={classes.QrRowLabel}>ФИО</div>
                                                    <div className={classes.QrRowValue}>
                                                        {qrModal.recipientName
                                                            ? qrModal.recipientName
                                                            : "ФИО будет показано в приложении банка"}
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        qrModal.recipientName &&
                                                        copyToClipboard(qrModal.recipientName)
                                                    }
                                                    disabled={!qrModal.recipientName}
                                                    className={
                                                        qrModal.recipientName
                                                            ? classes.QrCopyBtn
                                                            : classes.QrCopyBtnDisabled
                                                    }
                                                >
                                                    <CopyIcon/>
                                                </button>
                                            </div>

                                            <div className={classes.QrRow}>
                                                <div className={classes.QrRowMain}>
                                                    <div className={classes.QrRowLabel}>₽</div>
                                                    <div className={classes.QrRowValueStrong}>
                                                        {qrModal.amount.toLocaleString("ru-RU")} ₽
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        copyToClipboard(String(qrModal.amount))
                                                    }
                                                    className={classes.QrCopyBtn}
                                                >
                                                    <CopyIcon/>
                                                </button>
                                            </div>
                                        </div>

                                        {qrModal.bank && instructionImages[qrModal.bank] && (
                                            <InstructionCarousel
                                                bank={qrModal.bank}
                                                images={instructionImages[qrModal.bank]}
                                            />
                                        )}

                                        <div className={classes.LoadingSubtext}>
                                            Или нажмите на кнопку ниже, чтобы открыть страницу
                                            оплаты на этом устройстве.
                                        </div>
                                        <button
                                            type="button"
                                            className={classes.CreateBtn}
                                            onClick={() => {
                                                window.location.href = qrModal.url;
                                            }}
                                        >
                                            Открыть страницу оплаты
                                        </button>
                                        <button
                                            type="button"
                                            className={classes.SecondaryBtn}
                                            onClick={() => {
                                                setQrModal(null);
                                                setPaymentStatus(null);
                                                stopPolling();
                                                stopTimer();
                                            }}
                                        >
                                            Отмена
                                        </button>
                                    </React.Fragment>
                                )}
                            </React.Fragment>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}



// WEBPACK FOOTER //
// ./src/PagesOfCommon/Pay3/index.jsx